/**
 * Maps API error codes (from @naya/shared error classes) to short, human
 * strings for the admin UI. Unknown codes fall back to a generic message.
 * Inline field validation is handled by RHF/Zod — these cover server errors.
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Generic
  VALIDATION_ERROR: 'Some fields are invalid — check the form and try again.',
  UNAUTHORIZED: 'Your session has ended — please sign in again.',
  FORBIDDEN: "You don't have permission to do that.",
  NOT_FOUND: "That record doesn't exist.",
  CONFLICT: 'That conflicts with an existing record.',
  NETWORK_ERROR: 'Network request failed — check your connection and retry.',

  // Slug
  SLUG_ALREADY_TAKEN: 'That slug is already in use — pick another.',
  SLUG_RESERVED: 'That slug is reserved — pick another.',
  SLUG_INVALID_FORMAT:
    'Slug must be 3–30 lowercase letters, numbers, or hyphens, starting with a letter.',

  // Admin auth
  ADMIN_AUTH_INVALID: 'Incorrect email or password.',
  ADMIN_THROTTLED: 'Too many attempts — try again later.',
  ADMIN_SESSION_EXPIRED: 'Your session has expired — please sign in again.',
  ADMIN_NOT_IMPERSONATING:
    'Admin cannot access tenant routes without impersonating.',
  ADMIN_MUST_EXIT_IMPERSONATION:
    'Exit impersonation before accessing admin routes.',
  ADMIN_PASSWORD_CHANGE_REQUIRED:
    'Change your temporary password before continuing.',

  // Impersonation / tenant lifecycle
  IMPERSONATION_TARGET_INVALID: 'That user cannot be impersonated.',
  TENANT_SUSPENDED: 'This tenant is suspended.',
  TENANT_PENDING_SETUP: 'This tenant is still in setup.',

  // Invoices
  INVOICE_ALREADY_PAID: 'That invoice is already marked paid.',
  INVOICE_VOIDED: 'That invoice has been voided.',

  // Plan tiers
  PLAN_TIER_NOT_FOUND: 'That plan tier was not found.',

  // Branches (slice 3c)
  BRANCH_ARCHIVED: 'That branch is archived and cannot accept new stamps.',
  BRANCH_ALREADY_ARCHIVED: 'That branch is already archived.',
  BRANCH_NOT_ARCHIVED: 'That branch is not archived.',

  // Admin management (slice 3c)
  CANNOT_DEACTIVATE_SELF: 'You cannot deactivate your own admin account.',
  CANNOT_DEACTIVATE_LAST_ADMIN:
    'Cannot deactivate the last active admin — create another first.',
  ADMIN_EMAIL_TAKEN: 'An admin with that email already exists.',
  INCORRECT_PASSWORD: 'Current password is incorrect.',
  INVALID_IMAGE: 'That image is not valid.',
}

const FALLBACK = 'Something went wrong — try again.'

export function errorMessageFor(code: string): string {
  return ERROR_MESSAGES[code] ?? FALLBACK
}
